/**
 * Map Layers chat selection helpers.
 *
 * The picker stores a client-side selection per layer ({kind, id, name} plus
 * authType/serviceUrl so this module can find the matching ArcGIS portal
 * credential). At send time the outgoing wire shape is reduced to {kind, id,
 * name} — with a short-lived arcgisUserToken attached for signed-in userAuth
 * layers — and the local/history copy is reduced to {kind, id, name} only, so
 * live tokens and service URLs are never re-sent with message history or
 * persisted anywhere. The server resolves service URLs from its own DB row and
 * ignores any client-supplied URL.
 */

import IdentityManager from '@arcgis/core/identity/IdentityManager';

export type ChatSelectedMapLayer = {
  kind: 'internal' | 'external';
  id: string;
  name: string;
  /**
   * Client-side only — credential lookup + picker display. Stripped from every
   * outgoing payload.
   */
  authType?: 'apiKey' | 'userAuth' | 'public';
  serviceUrl?: string | null;
};

type RawSelection = { source?: string; selectedLayers?: unknown[] } & Record<
  string,
  unknown
>;

/**
 * Resolve a short-lived ArcGIS user token for server-side queries of a secured
 * layer. Only uses credentials registered for the layer/service URL — an
 * unrelated portal credential often fails server-side as Invalid Token. Never
 * log the token value.
 */
export function findArcGisUserToken(
  serviceUrl?: string | null
): string | undefined {
  if (!serviceUrl || typeof serviceUrl !== 'string') return undefined;

  const candidates = [
    serviceUrl,
    serviceUrl.replace(/\/$/, ''),
    serviceUrl.replace(/\/\d+\/?$/, ''),
    serviceUrl.replace(/\/(FeatureServer|MapServer)\/?\d*\/?$/i, '/$1'),
    serviceUrl.replace(/\/(FeatureServer|MapServer)\/.*$/i, '/$1'),
  ];

  for (const url of candidates) {
    try {
      const cred = IdentityManager.findCredential(url);
      if (cred?.token) return String(cred.token);
    } catch {
      /* try next candidate */
    }
  }

  return undefined;
}

function baseLayerFields(layer: ChatSelectedMapLayer) {
  return { kind: layer.kind, id: layer.id, name: layer.name };
}

function isMapLayersSelection(selection: unknown): selection is RawSelection {
  return (
    !!selection &&
    typeof selection === 'object' &&
    (selection as RawSelection).source === 'Map Layers' &&
    Array.isArray((selection as RawSelection).selectedLayers)
  );
}

/** A secured (userAuth) external layer as the layers API lists it. */
export type SecuredLayerRef = { id: string; serviceUrl?: string | null };

/**
 * Outgoing wire shape for this turn: {kind, id, name} per layer, plus a
 * per-turn arcgisUserToken for userAuth layers the user is signed in to. COMS
 * threads the token through the tool context in memory only and scrubs it
 * before any message is persisted.
 *
 * `securedLayers` is the workspace's userAuth layer list. Selections restored
 * from chat history hold {kind, id, name} only, so whether a layer is secured —
 * and which service URL its credential is registered under — is resolved from
 * that list first, falling back to fields a fresh picker selection carries.
 */
export function prepareMapLayerSelectionsForSend<T>(
  realtimeSelections: T,
  securedLayers: SecuredLayerRef[] = []
): T {
  if (!Array.isArray(realtimeSelections)) return realtimeSelections;
  const securedById = new Map(
    securedLayers.map((layer) => [String(layer.id), layer])
  );
  return realtimeSelections.map((selection) => {
    if (!isMapLayersSelection(selection)) return selection;
    return {
      ...selection,
      selectedLayers: (selection.selectedLayers as ChatSelectedMapLayer[]).map(
        (layer) => {
          const base = baseLayerFields(layer);
          if (layer.kind !== 'external') return base;
          const secured = securedById.get(layer.id);
          if (!secured && layer.authType !== 'userAuth') return base;
          const token = findArcGisUserToken(
            secured?.serviceUrl ?? layer.serviceUrl
          );
          return token ? { ...base, arcgisUserToken: token } : base;
        }
      ),
    };
  }) as T;
}

/**
 * History/local copy: {kind, id, name} only — no tokens, no service URLs — safe
 * to re-send and store.
 */
export function stripMapLayerClientFields<T>(realtimeSelections: T): T {
  if (!Array.isArray(realtimeSelections)) return realtimeSelections;
  return realtimeSelections.map((selection) => {
    if (!isMapLayersSelection(selection)) return selection;
    return {
      ...selection,
      selectedLayers: (selection.selectedLayers as ChatSelectedMapLayer[]).map(
        baseLayerFields
      ),
    };
  }) as T;
}
